import { z } from 'zod';
import { ShortIdSchema, SlugSchema, TimestampSchema } from './primitives.js';
import { AgentTierSchema, type AgentTier } from './agent.js';

/**
 * Weekly agent leaderboard — the free-tier-safe rollup that ranks sales agents
 * by how many operators they signed in a given ISO week, per platform brand.
 *
 * Stored at `leaderboards/{platformBrandId}/weeks/{isoWeekKey}` (e.g.
 * `leaderboards/kilat/weeks/2026-W30`). It is a single denormalised aggregate
 * doc: the `leaderboardOnSignup` trigger applies a +1 integer bump to one
 * agent's entry per operator signup inside a transaction (mirroring the reviews
 * aggregate `onReviewWritten` — a delta, never a cross-agent scan). The read
 * path (a later UI PR) is therefore ONE bounded `get` on the week doc.
 *
 * The `entries` array is kept sorted DESCENDING by `signupCount` and capped at
 * `LEADERBOARD_TOP_N`, which bounds the doc size. Ranking beyond the top N is
 * intentionally not retained (see `applyLeaderboardSignup`).
 *
 * Access (`infra/firestore.rules`): PUBLIC read (the board is shown to agents,
 * like `platformBrands`), server-only write (the admin-SDK trigger).
 */

/** Cap on how many agents a single week's leaderboard doc retains. */
export const LEADERBOARD_TOP_N = 50;

export const AgentLeaderboardEntrySchema = z.object({
  agentId: ShortIdSchema.describe('`agents/{agentId}` doc id this row ranks.'),
  name: z
    .string()
    .min(1)
    .describe(
      'Agent display name, snapshotted from the agent profile at count time (denormalised so the read path needs no per-agent fetch).',
    ),
  tier: AgentTierSchema.describe('Agent tier (bronze|silver|gold), snapshotted at count time.'),
  signupCount: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Operators this agent signed in this ISO week — the v1 ranking signal.'),
  activeCount: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe(
      "Reserved secondary signal: of this week's signups, how many are now `active`. Seeded to 0; a status-transition maintainer is a deliberate FOLLOW-UP (not maintained by `leaderboardOnSignup`) to avoid weekly-bucket-attribution ambiguity + churn double-counting.",
    ),
  cohortMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .describe(
      'YYYY-MM the agent joined (G22). Lets the tile rank them among people who ' +
        'started when they did — a race a new agent can actually win, where a ' +
        'global top-50 only tells them they are behind people with a year of ' +
        'tenure. Optional: rows written before this existed simply show no cohort.',
    ),
  bestSignupCount: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      "The agent's best week so far, carried forward so the tile can show a " +
        'personal best without reading every past week (G22). This is the only ' +
        'framing that still works for the agent placed last, who is exactly the ' +
        'one most likely to quit.',
    ),
});
export type AgentLeaderboardEntry = z.infer<typeof AgentLeaderboardEntrySchema>;

export const AgentLeaderboardSchema = z.object({
  platformBrandId: SlugSchema.describe('Platform brand (ADR-0017) this leaderboard belongs to.'),
  weekKey: z
    .string()
    .regex(/^\d{4}-W\d{2}$/, 'ISO week key, e.g. 2026-W30')
    .describe(
      'ISO-8601 week key `YYYY-Www` (brand market tz) — matches the `weeks/{weekKey}` doc id.',
    ),
  updatedAt: TimestampSchema.describe('Server time of the most recent signup bump.'),
  entries: z
    .array(AgentLeaderboardEntrySchema)
    .max(LEADERBOARD_TOP_N)
    .default([])
    .describe(
      `Agent rows, sorted DESC by signupCount, capped at ${LEADERBOARD_TOP_N} (see applyLeaderboardSignup).`,
    ),
});
export type AgentLeaderboard = z.infer<typeof AgentLeaderboardSchema>;

/** Identity + rank-snapshot fields for the agent whose signup is being counted. */
export interface LeaderboardSignupInput {
  readonly agentId: string;
  readonly name: string;
  readonly tier: AgentTier;
  /** YYYY-MM the agent joined, for cohort ranking (G22). */
  readonly cohortMonth?: string;
  /** The agent's best week before this one, if already known. */
  readonly bestSignupCount?: number;
}

/**
 * Pure reducer: apply ONE operator signup to the week's entry list.
 *
 * - Bumps the agent's `signupCount` by 1 (refreshing the `name`/`tier`
 *   snapshot), or inserts a fresh row (`signupCount: 1`, `activeCount: 0`) when
 *   the agent has none yet.
 * - Re-sorts DESCENDING by `signupCount`, breaking ties by `agentId` so the
 *   order is deterministic (and so a redelivery, guarded upstream by the
 *   exactly-once marker, would never reorder ties non-deterministically).
 * - Truncates to `LEADERBOARD_TOP_N`.
 *
 * Bounded-doc tradeoff: an agent outside the top N is dropped, so a later
 * signup re-inserts them at `signupCount: 1` rather than their true running
 * total. This only ever under-counts rows below rank N (never the leaders the
 * board exists to show) and keeps the aggregate a single bounded doc.
 *
 * Exactly-once counting is the caller's responsibility (the deterministic
 * per-tenant marker in `leaderboardOnSignup`); this reducer is deliberately
 * side-effect-free so it can be unit-tested in isolation.
 */
export function applyLeaderboardSignup(
  entries: readonly AgentLeaderboardEntry[],
  signup: LeaderboardSignupInput,
): AgentLeaderboardEntry[] {
  const byAgent = new Map<string, AgentLeaderboardEntry>();
  for (const e of entries) byAgent.set(e.agentId, e);

  const prior = byAgent.get(signup.agentId);
  const signupCount = (prior?.signupCount ?? 0) + 1;
  byAgent.set(signup.agentId, {
    agentId: signup.agentId,
    name: signup.name,
    tier: signup.tier,
    signupCount,
    activeCount: prior?.activeCount ?? 0,
    // Carried forward so the tile can show a personal best without reading
    // every past week (G22). The running maximum only ever grows, so a bad
    // week never erases the record it is being measured against.
    ...(signup.cohortMonth ? { cohortMonth: signup.cohortMonth } : {}),
    ...(signup.bestSignupCount !== undefined || prior?.bestSignupCount !== undefined
      ? {
          bestSignupCount: Math.max(
            signupCount,
            signup.bestSignupCount ?? 0,
            prior?.bestSignupCount ?? 0,
          ),
        }
      : { bestSignupCount: signupCount }),
  });

  return [...byAgent.values()]
    .sort((a, b) => b.signupCount - a.signupCount || a.agentId.localeCompare(b.agentId))
    .slice(0, LEADERBOARD_TOP_N);
}
